import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Check, ChevronDown, FileText, Loader2, X } from 'lucide-react';
import type { ProjectFile, ProjectFileStream } from '../../types';
import { headVersionForStream, versionsForStream as streamVersions } from '../../utils/projectFileStreamVersions';
import {
  isHtmlType,
  isPdfType,
  subscribeProjectFiles,
  subscribeProjectFileStreams,
} from '../../utils/projectFileService';
import { portalHostedAssetHref, portalHtmlPreviewHref } from '../../utils/portalAssetUrl';
import { getPdfPageCount, renderPdfPageThumbUrl } from '../../utils/boardSnapshotCapture';
import './BoardSnapshotPicker.css';

export interface BoardSnapshotPdfChoice {
  url: string;
  pageNumber: number;
  label: string;
}

export interface BoardSnapshotModelChoice {
  url: string;
  fetchUrl: string;
  title: string;
  versionLabel?: string;
}

interface SnapshotPdfItem {
  key: string;
  title: string;
  subtitle?: string;
  /** Default (head) version. */
  file: ProjectFile;
  /** All versions in the stream, newest first (single entry for flat uploads). */
  versions: ProjectFile[];
}

interface SnapshotModelItem {
  key: string;
  title: string;
  subtitle?: string;
  /** Default (head) version. */
  file: ProjectFile;
  /** All versions in the stream, newest first (single entry for flat uploads). */
  versions: ProjectFile[];
}

interface BoardSnapshotPickerProps {
  open: boolean;
  projectId: string;
  importing?: boolean;
  importProgress?: { current: number; total: number } | null;
  onClose: () => void;
  onPickPdfPages: (choices: BoardSnapshotPdfChoice[]) => void;
  onPickModel: (choice: BoardSnapshotModelChoice) => void;
}

function isPdfFile(file: Pick<ProjectFile, 'mimeType' | 'name'>): boolean {
  return isPdfType(file.mimeType) || /\.pdf$/i.test(file.name || '');
}

function headForStream(stream: ProjectFileStream, files: ProjectFile[]): ProjectFile | undefined {
  return headVersionForStream(stream, files);
}

/** Short version tag for a stream file, e.g. "v3". */
function versionTag(file: ProjectFile): string | undefined {
  if (file.versionLabel) return `v${file.versionLabel}`;
  if (file.versionIndex != null) return `v${file.versionIndex}`;
  return undefined;
}

/** Version dropdown / caption: "v100.1 — first floor" (note, else file name). */
function versionOptionLabel(file: ProjectFile): string {
  const tag = versionTag(file);
  const note = (file.versionNote || '').trim();
  const fileName = (file.name || '').replace(/\.(html?|pdf)$/i, '').trim();
  const extra = note || fileName;
  if (tag && extra && extra.toLowerCase() !== tag.toLowerCase()) {
    return `${tag} — ${extra}`;
  }
  return tag || extra || 'Version';
}

/** All matching versions in a stream (incl. inherited pre-split history), newest first; always contains the head file. */
function versionsForStream(
  stream: ProjectFileStream,
  files: ProjectFile[],
  head: ProjectFile,
  matches: (f: ProjectFile) => boolean,
): ProjectFile[] {
  const list = streamVersions(stream, files).filter(f => f.id && f.url && matches(f));
  if (!list.some(f => f.id === head.id)) list.unshift(head);
  return list;
}

function buildLists(streams: ProjectFileStream[], files: ProjectFile[]): {
  pdfs: SnapshotPdfItem[];
  models: SnapshotModelItem[];
} {
  const pdfs: SnapshotPdfItem[] = [];
  const models: SnapshotModelItem[] = [];
  const usedIds = new Set<string>();

  for (const stream of streams) {
    const head = headForStream(stream, files);
    if (!head?.id || !head.url) continue;
    if (stream.streamKind === 'two_d_pdf' && isPdfFile(head)) {
      const versions = versionsForStream(stream, files, head, isPdfFile);
      versions.forEach(v => v.id && usedIds.add(v.id));
      pdfs.push({
        key: stream.id || head.id,
        title: stream.title || head.name,
        subtitle: versionTag(head),
        file: head,
        versions,
      });
    } else if (stream.streamKind === 'three_d_html' && isHtmlType(head.mimeType, head.name)) {
      const versions = versionsForStream(stream, files, head, f => isHtmlType(f.mimeType, f.name));
      versions.forEach(v => v.id && usedIds.add(v.id));
      models.push({
        key: stream.id || head.id,
        title: stream.title || head.name,
        subtitle: versionTag(head),
        file: head,
        versions,
      });
    }
  }

  for (const file of files) {
    if (!file.id || !file.url || usedIds.has(file.id)) continue;
    if (file.streamId || file.fileSection === 'quote') continue;
    if (isPdfFile(file)) {
      pdfs.push({ key: file.id, title: file.name, file, versions: [file] });
    } else if (isHtmlType(file.mimeType, file.name)) {
      models.push({ key: file.id, title: file.name, file, versions: [file] });
    }
  }

  return { pdfs, models };
}

function pageKey(pdfKey: string, page: number): string {
  return `${pdfKey}:${page}`;
}

const PdfPageGrid: React.FC<{
  url: string;
  title: string;
  importing?: boolean;
  selectedPages: Set<number>;
  onToggle: (pageNumber: number) => void;
  onSelectAll: (pageCount: number) => void;
  onClear: () => void;
}> = ({ url, title, importing, selectedPages, onToggle, onSelectAll, onClear }) => {
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [thumbs, setThumbs] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPageCount(null);
    setThumbs({});
    setError(null);
    void (async () => {
      try {
        const count = await getPdfPageCount(url);
        if (cancelled) return;
        setPageCount(count);
        const firstBatch = Math.min(count, 8);
        for (let i = 1; i <= firstBatch; i += 1) {
          try {
            const thumb = await renderPdfPageThumbUrl(url, i);
            if (cancelled) return;
            setThumbs(prev => (prev[i] ? prev : { ...prev, [i]: thumb }));
          } catch {
            /* skip missing thumb */
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load PDF pages.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  useEffect(() => {
    if (!pageCount || pageCount <= 8) return;
    let cancelled = false;
    void (async () => {
      for (let i = 9; i <= pageCount; i += 1) {
        if (cancelled) return;
        try {
          const thumb = await renderPdfPageThumbUrl(url, i);
          if (cancelled) return;
          setThumbs(prev => (prev[i] ? prev : { ...prev, [i]: thumb }));
        } catch {
          /* skip */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, pageCount]);

  if (error) {
    return <p className="board-snap-pages-error">{error}</p>;
  }
  if (pageCount == null) {
    return (
      <div className="board-snap-pages-loading">
        <Loader2 size={16} className="board-snap-spin" />
        Loading pages…
      </div>
    );
  }

  const selectedInFile = selectedPages.size;

  return (
    <div className="board-snap-pages-wrap">
      <div className="board-snap-pages-actions">
        <button
          type="button"
          className="board-snap-pages-link"
          disabled={importing}
          onClick={() => onSelectAll(pageCount)}
        >
          Select all pages
        </button>
        {selectedInFile > 0 && (
          <button
            type="button"
            className="board-snap-pages-link"
            disabled={importing}
            onClick={onClear}
          >
            Clear
          </button>
        )}
      </div>
      <div className="board-snap-pages">
        {Array.from({ length: pageCount }, (_, idx) => {
          const page = idx + 1;
          const selected = selectedPages.has(page);
          return (
            <button
              key={page}
              type="button"
              className={`board-snap-page${selected ? ' is-selected' : ''}`}
              disabled={importing}
              onClick={() => onToggle(page)}
              title={`${title} · p.${page}`}
              aria-pressed={selected}
            >
              {thumbs[page] ? (
                <img src={thumbs[page]} alt="" />
              ) : (
                <span className="board-snap-page-ph">p.{page}</span>
              )}
              <span className="board-snap-page-label">
                {selected && <Check size={11} strokeWidth={2.4} aria-hidden />}
                Page {page}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const BoardSnapshotPicker: React.FC<BoardSnapshotPickerProps> = ({
  open,
  projectId,
  importing,
  importProgress,
  onClose,
  onPickPdfPages,
  onPickModel,
}) => {
  const [streams, setStreams] = useState<ProjectFileStream[]>([]);
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedPdf, setExpandedPdf] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, BoardSnapshotPdfChoice>>({});
  /** Chosen version per item key (file id); defaults to the stream head. */
  const [versionByKey, setVersionByKey] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !projectId) return;
    setLoading(true);
    setExpandedPdf(null);
    setSelected({});
    setVersionByKey({});
    let streamsReady = false;
    let filesReady = false;
    const markReady = () => {
      if (streamsReady && filesReady) setLoading(false);
    };
    const unsubStreams = subscribeProjectFileStreams(projectId, list => {
      setStreams(list);
      streamsReady = true;
      markReady();
    });
    const unsubFiles = subscribeProjectFiles(projectId, list => {
      setFiles(list);
      filesReady = true;
      markReady();
    });
    return () => {
      unsubStreams();
      unsubFiles();
    };
  }, [open, projectId]);

  const { pdfs, models } = useMemo(() => buildLists(streams, files), [streams, files]);
  const selectedChoices = useMemo(() => Object.values(selected), [selected]);
  const selectedCount = selectedChoices.length;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !importing) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, importing]);

  /** Version the user picked for this item (falls back to the stream head). */
  const selectedVersionFor = (item: { key: string; file: ProjectFile; versions: ProjectFile[] }): ProjectFile => {
    const chosenId = versionByKey[item.key];
    if (chosenId) {
      const match = item.versions.find(v => v.id === chosenId);
      if (match) return match;
    }
    return item.file;
  };

  /** Snapshot caption base; includes the version (and note) when the stream has more than one. */
  const labelFor = (item: { title: string; versions: ProjectFile[] }, version: ProjectFile): string => {
    const tag = versionOptionLabel(version);
    return item.versions.length > 1 && tag ? `${item.title} ${tag}` : item.title;
  };

  const togglePage = (item: SnapshotPdfItem, href: string, pageNumber: number, baseLabel: string) => {
    const key = pageKey(item.key, pageNumber);
    setSelected(prev => {
      if (prev[key]) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return {
        ...prev,
        [key]: {
          url: href,
          pageNumber,
          label: `${baseLabel} · p.${pageNumber}`,
        },
      };
    });
  };

  const selectAllPages = (item: SnapshotPdfItem, href: string, pageCount: number, baseLabel: string) => {
    setSelected(prev => {
      const next = { ...prev };
      for (let page = 1; page <= pageCount; page += 1) {
        next[pageKey(item.key, page)] = {
          url: href,
          pageNumber: page,
          label: `${baseLabel} · p.${page}`,
        };
      }
      return next;
    });
  };

  const clearPdfPages = (pdfKey: string) => {
    const prefix = `${pdfKey}:`;
    setSelected(prev => {
      const next: Record<string, BoardSnapshotPdfChoice> = {};
      for (const [key, value] of Object.entries(prev)) {
        if (!key.startsWith(prefix)) next[key] = value;
      }
      return next;
    });
  };

  const pagesForPdf = (pdfKey: string): Set<number> => {
    const prefix = `${pdfKey}:`;
    const pages = new Set<number>();
    for (const key of Object.keys(selected)) {
      if (!key.startsWith(prefix)) continue;
      const page = Number(key.slice(prefix.length));
      if (Number.isFinite(page)) pages.add(page);
    }
    return pages;
  };

  if (!open) return null;

  const addLabel = importing && importProgress
    ? `Adding ${importProgress.current} of ${importProgress.total}…`
    : selectedCount === 1
      ? 'Add 1 snapshot'
      : `Add ${selectedCount} snapshots`;

  // Portaled to <body>: hosts like StructureDetailModal have their own fixed
  // overlay stacking contexts that would otherwise trap this picker behind them.
  return createPortal(
    <div className="board-snap-backdrop" onClick={importing ? undefined : onClose} role="presentation">
      <div
        className="board-snap-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Add plan or model snapshot"
        onClick={e => e.stopPropagation()}
      >
        <div className="board-snap-header">
          <div>
            <h2>Plans / Model</h2>
            <p>Select one or more 2D sheets, or open a 3D model to capture several views.</p>
          </div>
          <button type="button" className="board-snap-close" onClick={onClose} aria-label="Close" disabled={importing}>
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="board-snap-empty">
            <Loader2 size={18} className="board-snap-spin" />
            Loading project files…
          </div>
        ) : (
          <div className="board-snap-body">
            <section className="board-snap-section">
              <h3>
                <FileText size={14} />
                2D plans
              </h3>
              {pdfs.length === 0 ? (
                <p className="board-snap-empty-copy">No 2D PDFs on this project yet.</p>
              ) : (
                <ul className="board-snap-list">
                  {pdfs.map(item => {
                    const openPages = expandedPdf === item.key;
                    const version = selectedVersionFor(item);
                    const href = portalHostedAssetHref(version.url) || version.url;
                    const baseLabel = labelFor(item, version);
                    const selectedPages = pagesForPdf(item.key);
                    const hasVersions = item.versions.length > 1;
                    return (
                      <li key={item.key}>
                        <div className="board-snap-row-line">
                          <button
                            type="button"
                            className={`board-snap-row${openPages ? ' is-open' : ''}`}
                            onClick={() => setExpandedPdf(openPages ? null : item.key)}
                          >
                            <FileText size={16} />
                            <span className="board-snap-row-text">
                              <span className="board-snap-row-title">{item.title}</span>
                              {!hasVersions && item.subtitle ? (
                                <span className="board-snap-row-sub">{item.subtitle}</span>
                              ) : null}
                            </span>
                            {selectedPages.size > 0 && (
                              <span className="board-snap-row-count">{selectedPages.size}</span>
                            )}
                            <ChevronDown size={14} className="board-snap-chevron" />
                          </button>
                          {hasVersions && (
                            <select
                              className="board-snap-version"
                              value={version.id}
                              disabled={importing}
                              aria-label={`${item.title} version`}
                              onChange={e => {
                                const fileId = e.target.value;
                                setVersionByKey(prev => ({ ...prev, [item.key]: fileId }));
                                // Selected pages reference the previous version's URL.
                                clearPdfPages(item.key);
                              }}
                            >
                              {item.versions.map(v => (
                                <option key={v.id} value={v.id}>
                                  {versionOptionLabel(v)}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        {openPages && (
                          <PdfPageGrid
                            url={href}
                            title={baseLabel}
                            importing={importing}
                            selectedPages={selectedPages}
                            onToggle={pageNumber => togglePage(item, href, pageNumber, baseLabel)}
                            onSelectAll={count => selectAllPages(item, href, count, baseLabel)}
                            onClear={() => clearPdfPages(item.key)}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="board-snap-section">
              <h3>
                <Box size={14} />
                3D models
              </h3>
              {models.length === 0 ? (
                <p className="board-snap-empty-copy">No 3D HTML models on this project yet.</p>
              ) : (
                <ul className="board-snap-list">
                  {models.map(item => {
                    const version = selectedVersionFor(item);
                    const hasVersions = item.versions.length > 1;
                    return (
                      <li key={item.key}>
                        <div className="board-snap-row-line">
                          <button
                            type="button"
                            className="board-snap-row"
                            disabled={importing}
                            onClick={() => {
                              onPickModel({
                                url: portalHtmlPreviewHref(version.url, {
                                  downloadName: version.name,
                                  sizeBytes: version.size,
                                }),
                                fetchUrl: version.url,
                                title: item.title,
                                versionLabel: versionOptionLabel(version),
                              });
                            }}
                          >
                            <Box size={16} />
                            <span className="board-snap-row-text">
                              <span className="board-snap-row-title">{item.title}</span>
                              {!hasVersions && item.subtitle ? (
                                <span className="board-snap-row-sub">{item.subtitle}</span>
                              ) : null}
                            </span>
                            <span className="board-snap-row-action">Capture views</span>
                          </button>
                          {hasVersions && (
                            <select
                              className="board-snap-version"
                              value={version.id}
                              disabled={importing}
                              aria-label={`${item.title} version`}
                              onChange={e => {
                                const fileId = e.target.value;
                                setVersionByKey(prev => ({ ...prev, [item.key]: fileId }));
                              }}
                            >
                              {item.versions.map(v => (
                                <option key={v.id} value={v.id}>
                                  {versionOptionLabel(v)}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        )}

        <div className="board-snap-footer">
          <button type="button" className="board-snap-footer-cancel" onClick={onClose} disabled={importing}>
            Cancel
          </button>
          <button
            type="button"
            className="board-snap-footer-add"
            disabled={importing || selectedCount === 0}
            onClick={() => onPickPdfPages(selectedChoices)}
          >
            {importing && <Loader2 size={14} className="board-snap-spin" />}
            {addLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BoardSnapshotPicker;
